import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Typography,
  Tabs,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import GeneralSettings from "../AgentTabs/GeneralSettings";
import ToolsTab from "../AgentTabs/ToolsTab";
import KnowledgeTab from "../AgentTabs/KnowledgeTab";
import ModelAndBehavior from "../AgentTabs/ModelAndBehavior";
import CloseIcon from "@mui/icons-material/Close";
import {
  isOnlyAlphabets,
  isOnlyAlphabetsNumbers,
} from "../../../Shared-ui/utils/Common/Common";
import { Agent } from "../../../Shared-ui/Types/Agent";
import { NotSelected } from "../../../Constant/Setting";
import { useDispatch } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { AGENT, USER_MEMBER_GROUPS } from "../../../Services/Endpoint";
import { GroupAddOutlined } from "@mui/icons-material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { agentErrInitial, agentInitial } from "./AgentInitialData";

interface MyComponentProps {
  open: boolean;
  handleClose: () => void;
  agent: Agent | null;
}

const CreateAgent: React.FC<MyComponentProps> = (props) => {

  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = React.useState(0);
  const theme = useTheme(); // Access the theme
  const [agent, setAgent] = useState<Agent>(agentInitial);
  const [AgentErr, setAgentErr] = useState(agentErrInitial);
  const [isEditing, setIsEditing] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [unSelectedGroups, setUnSelectedGroups] = useState<string[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);

  const isXsScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (props.agent && props.agent.id) {
      setAgent((preVal) => {
        return { ...preVal, ...props.agent };
      });
      getAgentGroups(props.agent.id);
      setIsEditing(true);
    }
  }, [props.agent]);

  useEffect(() => {
    if (props.open) {
      setTabIndex(0);
      getUserGroups();
    }
  }, [props.open]);

  // useEffect(() => {
  //   // Check if all required agent fields are filled (returns true/false)
  //   const requiredFieldsFilled =
  //     !!agent.name.trim() &&
  //     !!agent.description.trim() &&
  //     !!agent.role.trim() &&
  //     agent.model !== NotSelected &&
  //     !!agent.instructions.trim();

  //   setDisableSubmit(!requiredFieldsFilled);
  // }, [agent]);

  useEffect(() => {
    // Only check for Name, Description, and Role for the first tab
    if (tabIndex === 0) {
      setDisableSubmit(
        !agent.name.trim() || !agent.description.trim() || !agent.role.trim()
        || (
          agent.agent_metadata.enable_zeasecure &&
          (
            !agent.agent_metadata.zeasecure_api_key?.trim() ||
            agent.agent_metadata.zeasecure_api_key.trim().length !== 36 ||
            !agent.agent_metadata.zeasecure_api_key.trim().startsWith("zsk_")
          )
        )
      )
    } else {
      // For other tabs, keep your existing validation
      const requiredFieldsFilled =
        !!agent.name.trim() &&
        !!agent.description.trim() &&
        !!agent.role.trim() &&
        agent.model !== NotSelected &&
        !!agent.instructions.trim();

      setDisableSubmit(!requiredFieldsFilled);
    }
  }, [agent, tabIndex]);

  useEffect(() => {
    setAgentErr((prevErr) => {
      const newErr = { ...prevErr };
      let changed = false;

      if (
        prevErr.name.value &&
        agent.name.trim() &&
        isOnlyAlphabetsNumbers(agent.name)
      ) {
        newErr.name = { error: "", value: false };
        changed = true;
      }

      if (prevErr.description.value && agent.description.trim()) {
        newErr.description = { error: "", value: false };
        changed = true;
      }

      if (prevErr.role.value && agent.role.trim()) {
        newErr.role = { error: "", value: false };
        changed = true;
      }

      if (prevErr.model.value && agent.model !== NotSelected) {
        newErr.model = { error: "", value: false };
        changed = true;
      }

      if (prevErr.instructions.value && agent.instructions.trim()) {
        newErr.instructions = { error: "", value: false };
        changed = true;
      }

      return changed ? newErr : prevErr;
    });
  }, [agent]);

  const createAgent = async () => {
    if (!validateAgent()) return;
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.post(AGENT, agent).then(
        (res) => res?.data
      );
      if (agent.is_shared && selectedGroups.length > 0) {
        await shareAgentToGroup(data.id);
      }
      if (agent.is_shared && unSelectedGroups.length > 0) {
        await unShareAgentToGroup(data.id);
      }
      handleClose();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const getUserGroups = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(USER_MEMBER_GROUPS).then(
        (res) => res?.data
      );
      setUserGroups(data || []);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const getAgentGroups = async (agentId: string) => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(
        `${AGENT}/${agentId}/shared-groups`
      ).then((res) => res?.data);
      setSelectedGroups(
        data.map(
          (group: { user_group_id: any; id: string }) => group.user_group_id
        ) || []
      );
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const shareSingleAgentToGroup = async (agentId: string, groupId: string) => {
    try {
      const data = await BaseInstance.post(
        `${AGENT}/${agentId}/share/${groupId}`
      ).then((res) => res?.data);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
    }
  };

  const unShareSingleAgentToGroup = async (
    agentId: string,
    groupId: string
  ) => {
    try {
      const data = await BaseInstance.delete(
        `${AGENT}/${agentId}/share/${groupId}`
      ).then((res) => res?.data);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
    }
  };

  const shareAgentToGroup = async (agent_id: string) => {
    try {
      dispatch(StartLoading());
      const sharePromises = selectedGroups.map((groupId) =>
        shareSingleAgentToGroup(agent_id, groupId)
      );
      await Promise.all(sharePromises);
    } catch (error) {
      console.error("Error sharing agent:", error);
    } finally {
      dispatch(StopLoading());
    }
  };

  const unShareAgentToGroup = async (agent_id: string) => {
    try {
      dispatch(StartLoading());
      const unSharePromises = unSelectedGroups.map((groupId) =>
        unShareSingleAgentToGroup(agent_id, groupId)
      );
      await Promise.all(unSharePromises);
    } catch (error) {
      console.error("Error sharing agent:", error);
    } finally {
      dispatch(StopLoading());
    }
  };

  const unShareIsSharedToggle = async (agent_id: string) => {
    try {
      dispatch(StartLoading());
      const unSharePromises = selectedGroups.map((groupId) =>
        unShareSingleAgentToGroup(agent_id, groupId)
      );
      await Promise.all(unSharePromises);
    } catch (error) {
      console.error("Error sharing agent:", error);
    } finally {
      dispatch(StopLoading());
    }
  };

  const updateAgent = async () => {
    if (!validateAgent()) return;
    if (!agent.id) return;
    try {
      dispatch(StartLoading());
      if (!agent.is_shared && selectedGroups.length > 0) {
        await unShareIsSharedToggle(agent.id);
      }
      const data = await BaseInstance.put(`${AGENT}/${agent.id}`, agent).then(
        (res) => res?.data
      );
      if (agent.is_shared && selectedGroups.length > 0) {
        await shareAgentToGroup(agent.id);
      }
      if (agent.is_shared && unSelectedGroups.length > 0) {
        await unShareAgentToGroup(agent.id);
      }
      handleClose();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const validateAgent = () => {
    let error = 0;
    let showGeneralTabError = false;
    const setError = (field: string, condition: boolean, message: string) => {
      if (condition) {
        setAgentErr((prev) => ({
          ...prev,
          [field]: { error: message, value: true },
        }));
        error += 1;
        if (["name", "description", "role"].includes(field)) {
          showGeneralTabError = true;
        }
        if (field === "instructions" || field === "model") {
          dispatch(ShowError(message));
        }
      } else {
        setAgentErr((prev) => ({
          ...prev,
          [field]: { error: "", value: false },
        }));
      }
    };

    // name
    setError("name", !agent.name.trim(), "Please enter the name");
    setError(
      "name",
      !isOnlyAlphabetsNumbers(agent.name),
      "Please enter the name A-z"
    );

    // description
    setError(
      "description",
      !agent.description.trim(),
      "Please enter the description"
    );

    // role
    setError("role", !agent.role.trim(), "Please enter the role");

    // model
    // setError("provider", selectedProvider === NotSelected, "Please select the provider");
    setError("model", agent.model === NotSelected, "Please select the model");

    // instructions
    setError(
      "instructions",
      !agent.instructions.trim(),
      "Please enter the instructions"
    );
    setError(
      "zeasecure_api_key",
      agent.agent_metadata?.enable_zeasecure &&
      (
        !agent.agent_metadata?.zeasecure_api_key ||
        agent.agent_metadata?.zeasecure_api_key.trim() === "" ||
        agent.agent_metadata?.zeasecure_api_key.length !== 36 ||
        !agent.agent_metadata?.zeasecure_api_key.startsWith("zsk_")
      ),
      "Please enter a valid zeasSecure API key (must start with 'zsk_' and be 36 characters long)"
    )
    //
    // If general tab has any error, show toast and redirect
    if (showGeneralTabError) {
      setTabIndex(0);
      dispatch(ShowError("Please fill all required fields"));
    }

    return error === 0 ? true : false;
  };

  const handleClose = () => {
    props.handleClose();
    setAgent(agentInitial);
    setAgentErr(agentErrInitial);
    setIsEditing(false);
    setDisableSubmit(true);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue < tabIndex) {
      setTabIndex(newValue);
    }
  };

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing(2), // Consistent padding from theme
        }}
      >
        <Box>
          {isEditing ? "Agent Settings" : "Create a New Agent"}
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mt: 0.5 }}
          >
            Define agent’s details, tools, and advanced settings
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<SettingsOutlinedIcon />}
            iconPosition="start"
            label={isXsScreen ? "" : isEditing ? "General" : "General Settings"}
            sx={{ textTransform: "capitalize", minWidth: 0 }}
          />
          <Tab
            icon={<AutoAwesomeIcon />}
            iconPosition="start"
            label={isXsScreen ? "" : "Model & Behavior"}
            sx={{ textTransform: "capitalize", minWidth: 0 }}
          />
          <Tab
            icon={<MenuBookOutlinedIcon />}
            iconPosition="start"
            label={isXsScreen ? "" : "Knowledge"}
            sx={{ textTransform: "capitalize", minWidth: 0 }}
          />
          <Tab
            icon={<BuildOutlinedIcon />}
            iconPosition="start"
            label={isXsScreen ? "" : "Tools"}
            sx={{ textTransform: "capitalize", minWidth: 0 }}
          />
        </Tabs>

        {/* Dynamic Tab Content */}
        <Box
          sx={{
            mt: 1,
            minHeight: 400, // Set your desired minimum height
            height: { xs: "auto", md: 400 }, // Optional: fixed height on desktop, auto on mobile
            p: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "stretch",
          }}
        >
          {tabIndex === 0 && (
            <GeneralSettings
              agent={agent}
              AgentErr={AgentErr}
              setAgent={setAgent}
              userGroups={userGroups}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              unSelectedGroups={unSelectedGroups}
              setUnSelectedGroups={setUnSelectedGroups}
            />
          )}
          {tabIndex === 1 && (
            <ModelAndBehavior
              agent={agent}
              AgentErr={AgentErr}
              setAgent={setAgent}
            />
          )}
          {tabIndex === 2 && (
            <KnowledgeTab
              Agent={agent}
              AgentErr={AgentErr}
              setAgent={setAgent}
            />
          )}
          {tabIndex === 3 && (
            <ToolsTab Agent={agent} AgentErr={AgentErr} setAgent={setAgent} />
          )}
        </Box>
      </DialogContent>

      {/* <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          color="primary"
          size="large"
          variant="outlined"
          sx={{
            textTransform: "capitalize",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          size="large"
          disabled={disableSubmit}
          color="primary"
          onClick={() => (isEditing ? updateAgent() : createAgent())}
          sx={{
            textTransform: "capitalize",
          }}
        >
          {isEditing ? "Update" : "Create"}
        </Button>
      </DialogActions> */}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          color="primary"
          size="large"
          variant="outlined"
          sx={{
            textTransform: "capitalize",
          }}
        >
          Cancel
        </Button>

        {/* Step 1: General Settings */}
        {tabIndex === 0 && (
          <Button
            variant="contained"
            disableElevation
            size="large"
            disabled={disableSubmit}
            color="primary"
            onClick={() => setTabIndex(1)}
            sx={{ textTransform: "capitalize" }}
          >
            Next
          </Button>
        )}

        {/* Step 2: Model & Behavior */}
        {tabIndex === 1 && (
          <Button
            variant="contained"
            disableElevation
            size="large"
            disabled={
              // Add your Model & Behavior validation logic here
              !agent.model ||
              agent.model === NotSelected ||
              !agent.instructions.trim()
            }
            color="primary"
            onClick={() => setTabIndex(2)}
            sx={{ textTransform: "capitalize" }}
          >
            Next
          </Button>
        )}

        {/* Step 3: Knowledge */}
        {tabIndex === 2 && (
          <Button
            variant="contained"
            disableElevation
            size="large"
            color="primary"
            onClick={() => setTabIndex(3)}
            sx={{ textTransform: "capitalize" }}
          >
            Next
          </Button>
        )}

        {/* Step 4: Tools */}
        {tabIndex === 3 && (
          <Button
            variant="contained"
            disableElevation
            size="large"
            color="primary"
            onClick={isEditing ? updateAgent : createAgent}
            sx={{ textTransform: "capitalize" }}
            disabled={disableSubmit}
          >
            {isEditing ? "Update" : "Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAgent;
